import React, { useEffect, useState } from 'react';
import { Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { StepperEnum, useWizardStepperContext } from '../../../../contexts/useWizardStepper.context';
import useWizardReportDataContext from '../../../../contexts/useWizardReportData.context';
import EmailChipsComponent from '../../../../../../../shared/components/email-chips-input/email-chips-input.component';
import { IWizardReportInfo } from '../../../../contracts/IWizardData';

// Updated schema
const schema = yup.object().shape({
    reportName: yup.string().required('Report Name is required'),
    reportDescription: yup.string(),
    permissions: yup.string().required('Permissions are required'),
    category: yup.string().required('Category is required'),
    emailRecipients: yup.string(),
    exportData: yup.string(),
    scheduleFrequency:yup.string().required('Schedule Frequency is required'),
    scheduleDate:yup.date().required('Schedule Date is required')   
});

/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export default function WizardReportDetailsComponent() {
    const { isNewReport, wizardData, applyReportData } = useWizardReportDataContext();
    const { activeStep, activeStepIndex, validateStep, setError } = useWizardStepperContext();
    const [resetTrigger, setResetTrigger] = useState<number>(0);
    const { control, handleSubmit, reset, watch,
        formState: { errors, isValid },
    } = useForm<IWizardReportInfo>({

        mode: 'all', // Validate on blur
        resolver: yupResolver(schema),
        defaultValues: {
            reportName: wizardData.reportName,
            reportDescription: wizardData.reportDescription,
            permissions: wizardData.permissions,
            category: wizardData.category,
            emailRecipients: wizardData.emailRecipients,
            exportData: wizardData.exportData,
            scheduleFrequency:wizardData.scheduleFrequency,
            scheduleDate:wizardData.scheduleDate
        }
    });

    useEffect(() => {
        setResetTrigger((prev) => prev + 1)
        reset({
            reportName: wizardData.reportName,
            reportDescription: wizardData.reportDescription,
            permissions: wizardData.permissions,
            category: wizardData.category,
            emailRecipients: wizardData.emailRecipients,
            exportData: wizardData.exportData,
            scheduleFrequency: wizardData.scheduleFrequency,
            scheduleDate:wizardData.scheduleDate
        });
    }, [wizardData])

    // Watch all fields and validate step dynamically
    useEffect(() => {
        if (activeStep === StepperEnum.Details) {
            validateStep(activeStepIndex, isValid);
        }
    }, [activeStep, isValid, validateStep]);

    // Persist valid changes to wizardData dynamically
    useEffect(() => {
        const subscription = watch((data) => {
            const isDataValid = schema.isValidSync(data)
            if (isDataValid || isNewReport) 
            applyReportData(data);
        });
        return () => subscription.unsubscribe();
    }, [watch, isValid, applyReportData]);

    function convertToDatetimeLocal(dateString :any) {
        if (!dateString) return ''; 
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    return (
        <form hidden={activeStep !== StepperEnum.Details} onSubmit={handleSubmit(() => { })}>
            <Grid container direction="column" spacing={2} sx={{ px: 9, pt: 9, pb: 3 }}>
                {/* Report Name & Description */}
                <Grid item container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Controller name="reportName" control={control}
                            render={({ field }) => (
                                <TextField fullWidth size="small" label="Report Name" variant="outlined"
                                    InputProps={{ autoComplete: 'off' }}
                                    error={!!errors.reportName}
                                    helperText={errors.reportName?.message}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller name="reportDescription" control={control}
                            render={({ field }) => (
                                <TextField fullWidth size="small" label="Report Description" variant="outlined"
                                    InputProps={{ autoComplete: 'off' }}
                                    error={!!errors.reportDescription}
                                    helperText={errors.reportDescription?.message}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </Grid>

                {/* Permissions & Category */}
                <Grid item container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Controller name="permissions" control={control}
                            render={({ field }) => (
                                <FormControl fullWidth size="small" variant="outlined" error={!!errors.permissions}>
                                    <InputLabel>Permissions</InputLabel>
                                    <Select {...field} label="Permissions">
                                        <MenuItem value="None">Private</MenuItem>
                                        <MenuItem value="Full Access">Full Access</MenuItem>
                                        <MenuItem value="Read Only">Read Only</MenuItem>
                                        <MenuItem value="View Only">View Only</MenuItem>
                                        <MenuItem value="Locked">Locked</MenuItem>
                                    </Select>
                                    {errors.permissions && (
                                        <span style={{ color: '#d32f2f', fontSize: '12px' }}>{errors.permissions.message}</span>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller name="category" control={control}
                            render={({ field }) => (
                                <FormControl fullWidth size="small" variant="outlined" error={!!errors.category}>
                                    <InputLabel>Category</InputLabel>
                                    <Select {...field} label="Category">
                                        <MenuItem value="Chargebacks">Chargebacks</MenuItem>
                                        <MenuItem value="Government Pricing">Government Pricing</MenuItem>
                                        <MenuItem value="Gross To Net">Gross To Net</MenuItem>
                                        <MenuItem value="Medicaid">Medicaid</MenuItem>
                                        <MenuItem value="MS Reports">MS Reports</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                        <MenuItem value="Sales Analytics">Sales Analytics</MenuItem>
                                    </Select>
                                    {errors.category && (
                                        <span style={{ color: '#d32f2f', fontSize: '12px' }}>{errors.category.message}</span>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Controller name="scheduleFrequency" control={control}
                            render={({ field }) => (
                                <FormControl fullWidth size="small" variant="outlined" error={!!errors.scheduleFrequency}>
                                    <InputLabel>Schedule Frequency</InputLabel>
                                    <Select {...field} label="Schedule Frequency">
                                            <MenuItem value="Every Day">Every Day</MenuItem>
                                            <MenuItem value="Every Weekday">Every Weekday</MenuItem>
                                            <MenuItem value="Every Week">Every Week</MenuItem>
                                            <MenuItem value="Every Two Weeks">Every Two Weeks</MenuItem>
                                            <MenuItem value="Every Month">Every Month</MenuItem>
                                            <MenuItem value="Every Quarter">Every Quarter</MenuItem>
                                            <MenuItem value="Alert Hourly">Alert Hourly</MenuItem>
                                            <MenuItem value="Alert Daily">Alert Daily</MenuItem>
                                    </Select>
                                    {errors.scheduleFrequency && (
                                        <span style={{ color: '#d32f2f', fontSize: '12px' }}>{errors.scheduleFrequency.message}</span>
                                    )}
                                </FormControl>
                            )}
                        />
                  </Grid>
                     <Grid item xs={12} sm={6}>
                            <Controller name="scheduleDate" control={control}
                                render={({ field }) => {
                                    const handleChange = (e:any) => {
                                        const rawValue = e.target.value;
                                        field.onChange(rawValue);
                                    };
                                    const formattedValue = field.value ? convertToDatetimeLocal(field.value) : '';
                                    return (
                                        <FormControl fullWidth size="small" error={!!errors.scheduleDate}>
                                            <TextField
                                                id="schedule-date"
                                                size="small"
                                                type="datetime-local"
                                                InputLabelProps={{ shrink: true }}
                                                value={formattedValue}
                                                onChange={handleChange}
                                                error={!!errors.scheduleDate}
                                            />
                                        </FormControl>
                                    );
                                }}
                             />
                          </Grid>
                      </Grid>
                {/* Email Recipients */}
                <Grid item xs={12}>
                    <Controller name="emailRecipients" control={control}
                        render={({ field, fieldState }) => (
                            <EmailChipsComponent label="Recipient Emails" placeholder="Enter a valid Recipient Email Address"
                                separator=","
                                resetTrigger={resetTrigger}
                                validationError={fieldState.error?.message} // Pass validation error
                                onErrorChange={(error) => setError(activeStepIndex, error)}
                                value={field.value || ''} // Controlled value
                                onChange={(value: string) => {
                                    field.onChange(value); // Sync with react-hook-form
                                }}
                            />
                        )}
                    />
                </Grid>

                {/* Export Data */}
                <Grid item xs={12}>
                    <Controller name="exportData" control={control}
                        render={({ field }) => (
                            <TextField fullWidth size="small" multiline rows={4} label="Export Data" variant="outlined"
                                error={!!errors.exportData}
                                helperText={errors.exportData?.message}
                                {...field}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </form>
    );
}